import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { Box, Breadcrumbs, Button, CircularProgress, Grid, Link, Typography, useTheme } from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';

import { CrystalButtonPopup, MostActiveZones, ZonesTabInBuilding, ZoneTabSearch } from 'components';
import { ROUTES } from 'consts';
import { downloadBlob, getBuildingLocation } from 'utils';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useLazyDownloadDecalQuery } from 'store/apis';
import { useParamValues } from 'hooks/url.hook';
import { useLoadingToast } from 'hooks/toasts/loading-toast.hook';
import { useBuilding, useActiveBuildingPage } from 'pages/buildings/buildings.hook';
import { useZoneFilters } from 'pages/zones/zones.hook';
import { BuildingDeleteDialog, BuildingUpdateDialog } from 'dialogs/building';
import { ZoneCreateDialog } from 'dialogs/zone';
import { downloadBuildingZoneDecals, retainSelectedBuildingZoneIds } from 'pages/buildings/building-zone-decals';
import { useExclusiveAsyncAction } from 'hooks/exclusive-async-action.hook';

export const BuildingDetailsLayout = () => {
  const { orgSlug, isAdmin } = useAppSelector(selectCurrentUserInfo);
  const { buildingId } = useParamValues(['buildingId']);
  const [loading, building] = useBuilding({ buildingId: Number(buildingId) });
  const navigate = useNavigate();
  const { isActivePage, tabKey } = useActiveBuildingPage();
  const { width } = useWindowSize();
  const theme = useTheme();
  const [visibleBuildingUpdateDialog, setVisibleBuildingUpdateDialog] = useState<boolean>(false);
  const [visibleBuildingDeleteDialog, setVisibleBuildingDeleteDialog] = useState<boolean>(false);
  const [visibleZoneCreateDialog, setVisibleZoneCreateDialog] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [downloadDecalTrigger] = useLazyDownloadDecalQuery();
  const loadingToast = useLoadingToast();
  const { isRunning: isDownloadingDecals, run: runDownloadAction } = useExclusiveAsyncAction();

  useEffect(() => {
    if (!loading && !building) {
      navigate(`/${orgSlug}/${ROUTES.BUILDINGS}`);
    }
  }, [loading, building]);

  const pageMenuItems = [
    {
      id: 1,
      label: 'New',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.BUILDING_CREATE}`);
      },
    },
    {
      id: 2,
      label: 'Edit',
      onClick: () => {
        setVisibleBuildingUpdateDialog(true);
      },
    },
    {
      id: 3,
      label: 'Delete',
      onClick: () => {
        setVisibleBuildingDeleteDialog(true);
      },
    },
    {
      id: 4,
      label: 'Add Zone',
      onClick: () => {
        setVisibleZoneCreateDialog(true);
      },
    },
  ];

  const {
    loading: zoneLoading,
    zones,
    filteredZones,
    filters,
    setSearch,
    setFilters,
    counts,
  } = useZoneFilters({ tabKey, buildingId: Number(buildingId) });

  useEffect(() => {
    setSelected((currentSelected) => {
      const retainedSelected = isActivePage ? retainSelectedBuildingZoneIds(currentSelected, filteredZones) : [];
      const selectionDidChange = retainedSelected.some((zoneId, index) => zoneId !== currentSelected[index]);

      return retainedSelected.length === currentSelected.length && !selectionDidChange
        ? currentSelected
        : retainedSelected;
    });
  }, [buildingId, filteredZones, isActivePage]);

  const handleDownloadDecals = React.useCallback(async () => {
    if (!selected.length) {
      return;
    }

    try {
      loadingToast.open({ message: 'Downloading Decal' });
      await downloadBuildingZoneDecals({
        selectedZoneIds: selected,
        zones: filteredZones,
        orgSlug,
        downloadDecal: downloadDecalTrigger,
        saveDecal: downloadBlob,
      });
    } finally {
      loadingToast.close();
    }
  }, [selected, filteredZones, orgSlug, loadingToast, downloadDecalTrigger]);

  return (
    <>
      <Box sx={{ padding: 3 }}>
        <Grid container rowSpacing={5} columnSpacing={3}>
          <Grid
            item
            xs={12}
            container
            spacing={2}
            alignItems={width < theme.breakpoints.values.md ? 'center' : 'flex-start'}
          >
            <Grid item xs={11} sm={7}>
              <Breadcrumbs
                separator={
                  <Box sx={{ paddingX: 1.5 }}>
                    <ArrowForwardIos sx={{ fontSize: 10 }} />
                  </Box>
                }
              >
                <Link
                  component="button"
                  variant="subtitle2"
                  color="textSecondary"
                  onClick={() => {
                    navigate(`/${orgSlug}/${ROUTES.BUILDINGS}`);
                  }}
                >
                  Buildings
                </Link>

                {loading ? (
                  <CircularProgress size={16} sx={{ marginTop: 0.5 }} />
                ) : (
                  <Typography variant="subtitle2" color="primary">
                    {building ? getBuildingLocation(building) : 'All'}
                  </Typography>
                )}
              </Breadcrumbs>
            </Grid>
            {isAdmin && (
              <Grid item container sx={{ justifyContent: 'flex-end' }} xs={1} sm={5}>
                <CrystalButtonPopup items={pageMenuItems} label="" />
              </Grid>
            )}

            <Grid item xs={12} sm={isActivePage ? 7 : 12}>
              <ZoneTabSearch filters={filters} zones={zones} onSearchChange={setSearch} onFiltersChange={setFilters} />
            </Grid>

            {isActivePage && (
              <Grid item xs={12} sm={5} display="flex" justifyContent="flex-end">
                <Button
                  sx={{
                    maxHeight: 50,
                    width: { xs: '100%', sm: 'auto' },
                    display: { xs: 'none', sm: 'block' },
                    borderRadius: '4px',
                  }}
                  disabled={selected.length === 0 || isDownloadingDecals}
                  variant="contained"
                  onClick={() => runDownloadAction(handleDownloadDecals)}
                >
                  Download All Decals
                </Button>
              </Grid>
            )}

            {false && (
              <Grid item xs={12}>
                <MostActiveZones />
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <ZonesTabInBuilding counts={counts} loading={zoneLoading} />
            <Outlet
              context={{
                loading: zoneLoading,
                selected,
                setSelected,
                zones: filteredZones,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <BuildingUpdateDialog
        visible={visibleBuildingUpdateDialog}
        setVisible={setVisibleBuildingUpdateDialog}
        buildingId={Number(buildingId)}
      />

      <BuildingDeleteDialog
        visible={visibleBuildingDeleteDialog}
        setVisible={setVisibleBuildingDeleteDialog}
        buildingId={Number(buildingId)}
      />

      <ZoneCreateDialog
        visible={visibleZoneCreateDialog}
        setVisible={setVisibleZoneCreateDialog}
        buildingId={buildingId ? Number(buildingId) : undefined}
      />
    </>
  );
};
